"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FabricMapProvider = void 0;
const lodash_1 = require("lodash");
const react_1 = __importDefault(require("react"));
const react_dom_1 = __importDefault(require("react-dom"));
const react_intl_1 = require("react-intl");
const translation_service_1 = require("../../services/translation-service");
const Context_1 = require("./Context");
const FabricMap_1 = require("./FabricMap");
const FabricMapSync_1 = require("./FabricMapSync");
require("mapbox-gl/dist/mapbox-gl.css");
/**
 * This `div` serves as a portal for the `FabricMap` component that `FabricMapProvider`
 * will use. `FabricMapHost` will then cause the div to be appended to specific elements in clients apps.
 */
const mapPortalDiv = document.createElement("div");
mapPortalDiv.className = "fabric-mapbox-host-container";
mapPortalDiv.dataset.qa = "fabric-mapbox-host-container";
mapPortalDiv.style.width = "100%";
mapPortalDiv.style.height = "100%";
mapPortalDiv.style.visibility = "hidden";
/**
 * This is the parent component for the singular FabricMap instance. It creates `<FabricMapSyncProvider>`
 * and `<FabricMapPortalContext.Provider>, then creates the map and puts it in the `mapPortalDiv`.
 *
 * It then appends the `mapPortalDiv` to elements specified by `FabricMapHost`.
 */
exports.FabricMapProvider = react_1.default.memo((props) => {
    /**
     * The provider uses this state to determine the map's props and where it will be rendered.
     *
     * `setMapState` will be called by `setElement`, which itself is passed into `FabricMapPortalContext`.
     */
    const [mapState, setMapState] = react_1.default.useState({});
    const parentRef = react_1.default.useRef();
    /**
     * Attempt to render the map either in provideAt or body. This is essentially a holding pen for the map until we get a containerId.
     */
    if (!parentRef.current) {
        if (props.provideAt) {
            parentRef.current =
                document.getElementById(props.provideAt) || undefined;
        }
        if (!parentRef.current) {
            parentRef.current = document.body;
        }
        parentRef.current.append(mapPortalDiv);
    }
    /**
     * Remove the map when the component un-mounts
     */
    react_1.default.useLayoutEffect(() => {
        return () => mapPortalDiv.remove();
    }, []);
    /**
     * When provided a container ID, move the map from the parent to the containerId and make it visible.
     * layoutEffect to wait for paint to be complete, then trigger resize to make sure map fills whole space.
     */
    react_1.default.useLayoutEffect(() => {
        if (mapState.containerId) {
            const container = document.getElementById(mapState.containerId);
            if (container) {
                container.appendChild(mapPortalDiv);
                mapPortalDiv.style.visibility = "visible";
                /**
                 * `FabricMap` has attached a resize listener to the window that calls `map.resize`.
                 * This synthetic event hijacks that listener to ensure that the map correctly fills its
                 * container.
                 *
                 * We do this because it's the easiest way to get at the map because we're outside the map context.
                 *
                 * (Yes, this is somewhat hacky. Forgive us.)
                 */
                window.dispatchEvent(new Event("resize"));
                if (!mapState.initialized) {
                    setMapState(Object.assign(Object.assign({}, mapState), { initialized: true }));
                }
            }
        }
    }, [mapState]);
    /**
     * When FabricMapProvider unmounts, move the mapPortalDiv to the parent and hide it.
     */
    react_1.default.useLayoutEffect(() => {
        return () => {
            if (parentRef.current) {
                mapPortalDiv.style.visibility = "hidden";
                parentRef.current.appendChild(mapPortalDiv);
            }
        };
    }, [mapState.containerId]);
    /**
     * This function is responsible for updating the location of the mapPortalDiv.
     *
     * If called with undefined, it will empty out the children and cause a useLayoutEffect to
     * move the mapPortalDiv out of a client app.
     *
     * It is passed into `FabricMapPortalContext` and called by `FabricMapHost`.
     */
    const setElement = react_1.default.useCallback((props) => {
        let targetState;
        if (props) {
            const { containerId, children } = props, fabricMapProps = __rest(props, ["containerId", "children"]);
            targetState = {
                hostChildren: children,
                containerId,
                initialized: mapState.initialized,
                fabricMapProps,
            };
        }
        else {
            targetState = {
                hostChildren: react_1.default.createElement(react_1.default.Fragment, null),
                initialized: true,
            };
        }
        setMapState(targetState);
    }, [mapState]);
    const { hostChildren, initialized, fabricMapProps } = mapState;
    const { locale } = props;
    return (react_1.default.createElement(react_intl_1.IntlProvider, { locale: locale, messages: translation_service_1.Translations[locale] },
        react_1.default.createElement(FabricMapSync_1.FabricMapSyncProvider, null,
            react_1.default.createElement(Context_1.FabricMapPortalContext.Provider, { value: setElement },
                initialized ? (react_dom_1.default.createPortal(react_1.default.createElement(FabricMap_1.FabricMap, Object.assign({}, fabricMapProps), hostChildren ? ((0, lodash_1.isFunction)(hostChildren) ? (hostChildren()) : (hostChildren)) : (react_1.default.createElement(react_1.default.Fragment, null))), mapPortalDiv)) : (react_1.default.createElement(react_1.default.Fragment, null)),
                props.children))));
});
exports.FabricMapProvider.displayName = "FabricMapProvider";
